import { useEffect, useRef, useState } from 'react';
import Debounce from '../CommonComponent/debounce';
import { dataService } from '../services/dataService';

const SLUG_FORMAT_REGEX = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;

export default function useVanitySlug({ auctionData, setAuctionData, setEditedAuctionData }) {
    const [slugError, setSlugError] = useState('');
    const [isFormatInvalid, setIsFormatInvalid] = useState(false);
    const [isTaken, setIsTaken] = useState(false);
    const [slugSuccess, setSlugSuccess] = useState('');
    const [checkingSlug, setCheckingSlug] = useState(false);
    const originalSlugRef = useRef('');
    const hasCapturedOriginalRef = useRef(false);
    const latestSlugRef = useRef('');

    useEffect(() => {
        if (!hasCapturedOriginalRef.current && auctionData._id) {
            originalSlugRef.current = (auctionData.vanity_slug || '').toLowerCase();
            hasCapturedOriginalRef.current = true;
        }
    }, [auctionData._id, auctionData.vanity_slug]);

    const checkSlugAvailability = useRef(Debounce(async (slug, auctionId) => {
        const result = await dataService.CheckSlugAvailability(slug, auctionId || '');
        if (latestSlugRef.current !== slug) {
            return;
        }
        setCheckingSlug(false);
        if (result.error) {
            setIsTaken(true);
            const message = result.message?.status === 403
                ? (result.message?.data?.message || 'This action is not permitted for your account.')
                : (result.message?.data?.message || 'This URL is unavailable, please try another');
            setSlugError(message);
            setSlugSuccess('');
        } else if (!result.data.available) {
            setIsTaken(true);
            setSlugError('This URL is unavailable, please try another');
            setSlugSuccess('');
        } else {
            setIsTaken(false);
            setSlugError('');
            setSlugSuccess('This URL is available');
        }
    })).current;

    const handleSlugChange = (rawValue) => {
        const slug = rawValue.toLowerCase();
        if (slug.length > 50) return;

        latestSlugRef.current = slug;
        setAuctionData((prevData) => ({ ...prevData, vanity_slug: slug }));
        setEditedAuctionData((prevData) => ({ ...prevData, vanity_slug: slug }));
        setSlugSuccess('');
        setIsTaken(false);

        if (slug === '') {
            checkSlugAvailability.cancel();
            setSlugError('');
            setIsFormatInvalid(false);
            setCheckingSlug(false);
            return;
        }
        if (slug === originalSlugRef.current) {
            checkSlugAvailability.cancel();
            setIsFormatInvalid(false);
            setIsTaken(false);
            setSlugError('');
            setSlugSuccess('');
            setCheckingSlug(false);
            return;
        }
        if (slug.length < 3 || !SLUG_FORMAT_REGEX.test(slug)) {
            checkSlugAvailability.cancel();
            setIsFormatInvalid(true);
            setCheckingSlug(false);
            setSlugError(slug.length < 3
                ? 'URL must be 3-50 characters.'
                : 'Lowercase letters, numbers and hyphens only');
            return;
        }
        setIsFormatInvalid(false);
        setSlugError('');
        setCheckingSlug(true);
        checkSlugAvailability(slug, auctionData._id);
    };

    return {
        slug: auctionData.vanity_slug || '',
        slugError,
        slugSuccess,
        checkingSlug,
        isFormatInvalid,
        isTaken,
        handleSlugChange,
        isSaveBlocked: Boolean(slugError) || checkingSlug,
    };
}
